import type { DependencyApiType } from '../../../gantt/dependencyDisplayUtils';
import type { LinkSnapshot, LinkedIssue, TaskSnapshot } from '../../../stores/types';
import { isOpenCriticalIssue } from '../../../utils/linkedIssueHelpers';
import { dependencyTypeFromDhtmlx } from '../../../utils/utils';

export type DependencyBlockerIssue = LinkedIssue;

export type DependencyBlockerRow = {
  /** Stable key: linkId|source|target */
  id: string;
  linkId: number;
  predecessorId: string;
  predecessorName: string;
  successorId: string;
  successorName: string;
  dependencyType: DependencyApiType;
  lag: number | null;
  /** Display e.g. FS, FS+2, SS-1 */
  dependencyLabel: string;
  issues: DependencyBlockerIssue[];
  causingDelay: boolean;
  successorPercentComplete: number | null;
  successorFinish: string | null;
};

export type DependencyBlockerKpis = {
  blockedSuccessorCount: number;
  blockingPredecessorCount: number;
  openCriticalIssueCount: number;
  currentlyCausingDelayCount: number;
};

export type DependencyBlockersReport = {
  kpis: DependencyBlockerKpis;
  rows: DependencyBlockerRow[];
};

function resolveTaskLabel(task: TaskSnapshot | undefined, id: string): string {
  const name = String(task?.taskName ?? '').trim();
  return name || `Task ${id}`;
}

function formatDependencyLabel(type: string, lag: number | null | undefined): {
  dependencyType: DependencyApiType;
  dependencyLabel: string;
} {
  const raw = dependencyTypeFromDhtmlx(type);
  const dependencyType = (['FS', 'SS', 'FF', 'SF'].includes(raw) ? raw : 'FS') as DependencyApiType;
  const lagNum = Number.isFinite(Number(lag)) ? Math.trunc(Number(lag)) : 0;
  const lagPart = lagNum === 0 ? '' : lagNum > 0 ? `+${lagNum}` : String(lagNum);
  return { dependencyType, dependencyLabel: `${dependencyType}${lagPart}` };
}

function openCriticalIssuesOnTask(task: TaskSnapshot | undefined): LinkedIssue[] {
  const linked = Array.isArray(task?.linkedIssues) ? task!.linkedIssues! : [];
  return linked.filter(isOpenCriticalIssue);
}

/**
 * Direct dependency blockers: successor tasks whose predecessor has ≥1 open Critical issue.
 * One row per predecessor→successor link (issues aggregated).
 */
export function deriveDependencyBlockers(
  tasks: TaskSnapshot[],
  links: LinkSnapshot[],
): DependencyBlockersReport {
  const taskById = new Map<string, TaskSnapshot>();
  for (const task of tasks) {
    taskById.set(String(task.id), task);
  }

  const rows: DependencyBlockerRow[] = [];

  for (const link of links) {
    const predecessorId = String(link.source);
    const successorId = String(link.target);
    if (!predecessorId || !successorId || predecessorId === successorId) continue;

    const predecessor = taskById.get(predecessorId);
    const issues = openCriticalIssuesOnTask(predecessor);
    if (issues.length === 0) continue;

    const successor = taskById.get(successorId);
    const { dependencyType, dependencyLabel } = formatDependencyLabel(link.type, link.lag);
    const causingDelay = issues.some((i) => i.currentlyAffected);

    rows.push({
      id: `${link.id}|${predecessorId}|${successorId}`,
      linkId: Number(link.id) || 0,
      predecessorId,
      predecessorName: resolveTaskLabel(predecessor, predecessorId),
      successorId,
      successorName: resolveTaskLabel(successor, successorId),
      dependencyType,
      lag: link.lag ?? null,
      dependencyLabel,
      issues,
      causingDelay,
      successorPercentComplete: successor?.percentComplete ?? null,
      successorFinish: successor?.finish ?? null,
    });
  }

  rows.sort((a, b) => {
    if (a.causingDelay !== b.causingDelay) return a.causingDelay ? -1 : 1;
    const bySucc = a.successorName.localeCompare(b.successorName);
    if (bySucc !== 0) return bySucc;
    return a.predecessorName.localeCompare(b.predecessorName);
  });

  const successorIds = new Set<string>();
  const predecessorIds = new Set<string>();
  const ticketIds = new Set<string>();
  const delayTicketIds = new Set<string>();

  for (const row of rows) {
    successorIds.add(row.successorId);
    predecessorIds.add(row.predecessorId);
    for (const issue of row.issues) {
      ticketIds.add(issue.ticketId);
      if (issue.currentlyAffected) delayTicketIds.add(issue.ticketId);
    }
  }

  return {
    kpis: {
      blockedSuccessorCount: successorIds.size,
      blockingPredecessorCount: predecessorIds.size,
      openCriticalIssueCount: ticketIds.size,
      currentlyCausingDelayCount: delayTicketIds.size,
    },
    rows,
  };
}
